import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControlName,
    NG_VALUE_ACCESSOR,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { LocationInterface } from '@app/core/interfaces/location.interface';
import { ClaimAppointmentInterface } from '@app/my-zone/pages/claims/store/entity/appointment.interface';
import { NotificationInterface, RelationDataService, RelationInterface } from 'outshared-lib';
import { Observable, Subscription } from 'rxjs';
import { AppointmentService } from '../../appointment.service';

@Component({
    selector: 'ins-locations-form',
    templateUrl: './locations-form.component.html',
    styleUrls: ['./locations-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: LocationsFormComponent,
            multi: true,
        },
    ],
})
export class LocationsFormComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() disabled = false;
    @Input() formControlName: FormControlName;
    @Input() fileSerialNumber: number;
    @Input() appointment: ClaimAppointmentInterface;
    @Input() damageRepairCode: string;

    @Output() readonly selectLocation: EventEmitter<LocationInterface> = new EventEmitter();

    public addressRelationId: string;
    public locationSubset: LocationInterface[] = [];
    public locationDiff: number;
    public notifications: NotificationInterface[] = [];
    public loading: boolean = false;
    public form: UntypedFormGroup;
    public changePostcodeButtonDisabled: boolean = true;
    public relation$: Observable<RelationInterface> = this.relationDataService.getRelation$();

    private location: LocationInterface[] = [];
    private locationEndpos: number = 4;
    private subscriptions = new Subscription();

    constructor(
        private service: AppointmentService,
        private relationDataService: RelationDataService,
    ) {}

    @HostBinding('style.opacity') get opacity(): number {
        return this.disabled ? 0.25 : 1;
    }

    public get value(): string {
        return this.addressRelationId;
    }

    ngOnInit(): void {

        const relationSubscription = this.relation$.subscribe((relation) => {
            this.createForm(relation.residential_address.postal_code);
            this.getLocations(relation.residential_address.postal_code);
        });

        this.subscriptions.add(relationSubscription);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public onChangePostalCode(): void {
        if (this.form.get('postcode').invalid) {
            return;
        }
        this.getLocations(this.form.value.postcode);
    }

    public onShowMoreClick(): boolean {
        const newIndex = this.locationEndpos = this.locationEndpos + 10;

        this.updateLocationsTo(newIndex);
        return false;
    }

    public onShowLessClick(): boolean {
        this.updateLocationsTo(4);
        return false;
    }

    public onAddressRelationChange(event): void {
        this.onChange(event.target.value);
    }

    /**
     * Control value accessor implementation
     */
    public onChange = (value: any) => {};

    public onTouched(): void {}

    public writeValue(value: any): void {
        if (value !== undefined) {
            this.addressRelationId = value;
            this.onChange(value);
        }
    }

    public registerOnChange(fn: (dateInput: any) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public get postcode(): AbstractControl {
        return this.form.get('postcode');
    }

    private createForm(postcode: string): void {
        this.form = new UntypedFormGroup({
            postcode: new UntypedFormControl({
                value: postcode,
                disabled: true,
            }, [Validators.required, Validators.minLength(6)]),
            adres_relatie_id: new UntypedFormControl('', [Validators.required]),
        });

        const subRelationId = this.form.controls.adres_relatie_id.valueChanges
            .subscribe((value) => {
                    if (this.location.length > 0) {
                        const location = this.location.filter((l) => l.business_relation_id === parseInt(value, 10));
                        this.selectLocation.emit(location[0]);
                    }
                },
            );
        this.subscriptions.add(subRelationId);
    }

    private getLocations(postalCode: string): void {
        const subService = this.service.requestAppointmentLocations(
            this.appointment, this.fileSerialNumber, this.appointment.netwerk_id, postalCode,
        ).subscribe(
            (locations: LocationInterface[]) => {
                this.onGetLocationsSuccess(locations);
                this.loading = false;
            }, (error: NotificationInterface[] | string) => {
                if (typeof error === 'string') {
                    this.notifications.push({
                        message_code: '0',
                        message_content: 'Geen vestigingen gevonden',
                        item: 'DispatchToJson',
                        notification_type: 'FOUT',
                    });
                } else {
                    this.notifications = error;
                }
                this.loading = false;
                this.form.controls.postcode.enable();
                this.changePostcodeButtonDisabled = false;
            },
        );
        this.subscriptions.add(subService);
        this.loading = true;
        this.resetForm();
    }

    private resetForm(): void {
        this.location = [];
        this.notifications = [];
        this.locationSubset = [];
        this.changePostcodeButtonDisabled = true;

        this.form.controls.postcode.disable();
        this.form.controls.adres_relatie_id.reset();

        this.writeValue(null);
    }

    private onGetLocationsSuccess(locations: LocationInterface[]): void {
        locations.forEach((location) => {
            this.location.push(this.createLocation(location));
        });
        this.updateLocationsTo(4);

        this.form.controls.postcode.enable();
        this.changePostcodeButtonDisabled = false;
    }

    private createLocation(location: LocationInterface): LocationInterface {

        return {
            business_relation_id: location.business_relation_id,
            distance: location.distance,
            latitude: location.latitude,
            email_address: location.email_address,
            civic_number: location.civic_number,
            civic_number_suffix: location.civic_number_suffix,
            longitude: location.longitude,
            name: location.name,
            city: location.city,
            postal_code: location.postal_code,
            street_name: location.street_name,
            telephone_number: location.telephone_number,
            network_id: (location.network_id) ? location.network_id.trim() : null,
        };
    }

    private updateLocationsTo(endpos: number): void {
        this.locationEndpos = endpos;

        if (this.locationEndpos >= this.location.length) {
            this.locationEndpos = this.location.length;
        }

        this.locationSubset = this.location.slice(0, this.locationEndpos);
        this.locationDiff = this.location.length - this.locationEndpos;
    }
}
